import { useNavigate } from "react-router";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { BALANCE_PRIVATE_MODE_SX } from "features/patients/sections/patient-billing/components/balance-display/balance-display.component";
import useBalanceDisplay from "features/patients/sections/patient-billing/components/balance-display/use-balance-display.hook";
import { usePatientPrivacy } from "hooks/use-patient-privacy.hook";
import { TinyInsurancePolicy } from "services/types/insurance-policies.type";
import { ManagedCarePlan } from "services/types/managed-care-plans.type";
import { Patient } from "services/types/patients.type";

type BalanceDisplayCompactProps = {
  patient: Patient;
  totalBalance: number;
  insurerResponsibilityBalance: number;
  patientResponsibilityBalance: number;
  managedCareResponsibilityBalance: number;
  managedCarePlan?: ManagedCarePlan;
  insurancePolicy?: TinyInsurancePolicy;
  paymentSourceType: number;
};

function BreakdownRow({
  label,
  value,
  color,
  isPrivateMode
}: {
  label: string;
  value: string;
  color: string;
  isPrivateMode: boolean;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: "4px",
        background: color,
        gap: 2
      }}
    >
      <Typography variant="caption" noWrap>
        {label}
      </Typography>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={isPrivateMode ? BALANCE_PRIVATE_MODE_SX : {}}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function BalanceDisplayCompact({
  patient,
  totalBalance,
  insurerResponsibilityBalance,
  patientResponsibilityBalance,
  managedCareResponsibilityBalance,
  managedCarePlan,
  insurancePolicy,
  paymentSourceType
}: BalanceDisplayCompactProps) {
  const navigate = useNavigate();
  const { isPrivateMode } = usePatientPrivacy();

  const {
    totalBalanceColor,
    totalBalanceText,
    patientResponsibilityBalanceColor,
    patientResponsibilityBalanceText,
    additionalBalance
  } = useBalanceDisplay({
    totalBalance,
    insurerResponsibilityBalance,
    patientResponsibilityBalance,
    managedCareResponsibilityBalance,
    managedCarePlan,
    insurancePolicy,
    paymentSourceType
  });

  const tooltipContent = (
    <Stack gap={0.5} sx={{ minWidth: 160 }}>
      <BreakdownRow
        label={additionalBalance.label}
        value={additionalBalance.value}
        color={additionalBalance.color}
        isPrivateMode={isPrivateMode}
      />
      <BreakdownRow
        label="Patient"
        value={patientResponsibilityBalanceText}
        color={patientResponsibilityBalanceColor}
        isPrivateMode={isPrivateMode}
      />
    </Stack>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="bottom"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 3,
            "& .MuiTooltip-arrow": { color: "background.paper" }
          }
        }
      }}
    >
      <Box
        data-testid={`sale-balance-color-${totalBalanceColor}`}
        onClick={() => navigate(`/patients/${patient.id}/billing`)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1.5,
          py: 0.5,
          background: totalBalanceColor,
          borderRadius: "5px",
          cursor: "pointer",
          boxShadow: "2px 2px 2px 0px rgba(0,0,0,0.15)"
        }}
      >
        <Typography
          fontWeight={700}
          fontSize="18px"
          data-testid="sale-balance-value"
          sx={isPrivateMode ? BALANCE_PRIVATE_MODE_SX : {}}
        >
          {totalBalanceText}
        </Typography>
      </Box>
    </Tooltip>
  );
}
